import Box from "@mui/material/Box";
import PaymentsIcon from "@mui/icons-material/Payments";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import InventoryIcon from "@mui/icons-material/Inventory";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StorefrontIcon from "@mui/icons-material/Storefront";
import type { IPagamentoLoja } from "interfaces/loja";
import type { PedidoOperacionalInput } from "interfaces/loja/pedidoOperacional";
import {
  labelStatusFaturamento,
  labelStatusLogistico,
  labelStatusRetirada,
  labelStatusSeparacao,
  pedidoExpedicaoPath,
  pedidoFaturamentoPath,
  pedidoRetiradaPath,
  pedidoSeparacaoPath,
  resolvePedidoOperacional,
} from "utils/pedidoOperacional";
import PedidoResumoEtapaCard from "./PedidoResumoEtapaCard";

function resumoPagamentoLabel(pagamentos: IPagamentoLoja[]): string {
  if (!pagamentos.length) return "Sem registros";
  const algumPago = pagamentos.some((p) => p.status === "PAGO");
  if (algumPago) return "Pagamento confirmado";
  const pend = pagamentos.filter(
    (p) => p.status === "PENDENTE" || p.status === "AUTORIZADO"
  );
  if (pend.length) return `Aguardando (${pend[0].status})`;
  return String(pagamentos[0].status);
}

type PedidoEtapasResumoProps = {
  input: PedidoOperacionalInput;
  pagamentos: IPagamentoLoja[];
  pedidoId?: string;
};

export default function PedidoEtapasResumo({
  input,
  pagamentos,
  pedidoId,
}: PedidoEtapasResumoProps) {
  const o = resolvePedidoOperacional(input);
  const isDigital = o.tipoEntrega === "DIGITAL";

  const logisticaValor = isDigital
    ? "Download"
    : o.tipoEntrega === "ENTREGA"
      ? labelStatusLogistico(o.statusLogistico)
      : "—";
  const logisticaHint = isDigital
    ? "Entrega digital — sem envio físico"
    : o.tipoEntrega === "ENTREGA"
      ? "Envio e rastreio"
      : "Aplicável só à entrega";

  const retiradaValor =
    o.tipoEntrega === "RETIRADA_LOJA" || o.tipoEntrega === "RETIRADA_EVENTO"
      ? o.statusRetirada
        ? labelStatusRetirada(o.statusRetirada)
        : "—"
      : "—";
  const retiradaHint =
    o.tipoEntrega === "RETIRADA_LOJA"
      ? "Retirada na loja"
      : o.tipoEntrega === "RETIRADA_EVENTO"
        ? "Retirada no evento"
        : "Aplicável à retirada";

  const cellSx = {
    minWidth: 0,
    height: "100%",
    display: "flex",
    flexDirection: "column" as const,
    "& > .MuiCard-root": { height: "100%" },
  };

  return (
    <Box
      sx={{
        display: "grid",
        width: "100%",
        gap: 2,
        alignItems: "stretch",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, minmax(0, 1fr))",
          md: "repeat(3, minmax(0, 1fr))",
          lg: "repeat(5, minmax(0, 1fr))",
        },
      }}
    >
      <Box sx={cellSx}>
        <PedidoResumoEtapaCard
          id="sec-resumo-pagamento"
          titulo="Pagamento"
          valor={resumoPagamentoLabel(pagamentos)}
          hint="Situação dos pagamentos do pedido"
          icone={<PaymentsIcon fontSize="small" />}
        />
      </Box>
      <Box sx={cellSx}>
        <PedidoResumoEtapaCard
          id="sec-faturamento"
          titulo="Faturamento"
          valor={
            isDigital
              ? o.statusFaturamento === "EMITIDO"
                ? "Dispensado (digital)"
                : labelStatusFaturamento(o.statusFaturamento)
              : labelStatusFaturamento(o.statusFaturamento)
          }
          hint={
            isDigital
              ? "Produto digital — sem NF-e manual"
              : "Nota / documento fiscal"
          }
          icone={<ReceiptLongIcon fontSize="small" />}
          linkTo={
            !isDigital && pedidoId ? pedidoFaturamentoPath(pedidoId) : undefined
          }
          linkLabel={!isDigital && pedidoId ? "Abrir faturamento" : undefined}
        />
      </Box>
      <Box sx={cellSx}>
        <PedidoResumoEtapaCard
          id="sec-separacao"
          titulo="Separação"
          valor={
            isDigital
              ? o.statusSeparacao === "SEPARADO"
                ? "Não aplicável"
                : labelStatusSeparacao(o.statusSeparacao)
              : labelStatusSeparacao(o.statusSeparacao)
          }
          hint={isDigital ? "Produto digital — sem picking" : "Estoque e picking"}
          icone={<InventoryIcon fontSize="small" />}
          linkTo={
            !isDigital && pedidoId ? pedidoSeparacaoPath(pedidoId) : undefined
          }
          linkLabel={!isDigital && pedidoId ? "Abrir separação" : undefined}
        />
      </Box>
      <Box sx={cellSx}>
        <PedidoResumoEtapaCard
          id="sec-resumo-logistica"
          titulo="Logística"
          valor={logisticaValor}
          hint={logisticaHint}
          icone={<LocalShippingIcon fontSize="small" />}
          destaque={
            o.tipoEntrega === "ENTREGA" && o.statusSeparacao === "SEPARADO"
          }
          linkTo={
            pedidoId && o.tipoEntrega === "ENTREGA"
              ? pedidoExpedicaoPath(pedidoId)
              : undefined
          }
          linkLabel={
            pedidoId && o.tipoEntrega === "ENTREGA"
              ? "Abrir expedição"
              : undefined
          }
        />
      </Box>
      <Box sx={cellSx}>
        <PedidoResumoEtapaCard
          id="sec-retirada"
          titulo="Retirada"
          valor={retiradaValor}
          hint={retiradaHint}
          icone={<StorefrontIcon fontSize="small" />}
          destaque={
            (o.tipoEntrega === "RETIRADA_LOJA" ||
              o.tipoEntrega === "RETIRADA_EVENTO") &&
            o.statusSeparacao === "SEPARADO"
          }
          linkTo={
            pedidoId &&
            (o.tipoEntrega === "RETIRADA_LOJA" ||
              o.tipoEntrega === "RETIRADA_EVENTO")
              ? pedidoRetiradaPath(pedidoId)
              : undefined
          }
          linkLabel={
            pedidoId &&
            (o.tipoEntrega === "RETIRADA_LOJA" ||
              o.tipoEntrega === "RETIRADA_EVENTO")
              ? "Abrir retirada"
              : undefined
          }
        />
      </Box>
    </Box>
  );
}
